import { useState, useEffect } from 'react';
import Image from 'next/image';

import { isLenderStepCompleted } from '../EmiPlans/helper';
import Modal from '@/lib/Modal';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';

import { useGlobalStore } from '@/store';

import { fetchCustomerDetails } from '@/api/customer';

import { CUSTOMER_ACCESS_TOKEN_KEY, EMANDATE_POLLING_INTERVAL, LENDER_MAPPING } from '@/constants';
import { LENDER_NAMES } from '@/api/types';
import { getLenderDetailsFromTenure } from '@/utils/helpers';
import ErrorModal from '@/shared/Components/ErrorModal';
import { getEmandateStatus } from '@/api/kyc';

const EMandatePollingPopup = ({
  isOpen,
  onDismiss,
  onSuccess,
  onRetry,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
  onRetry: () => void;
}): JSX.Element => {
  const { userAccessToken, selectedTenure, repaymentId } = useGlobalStore();

  const [state, setState] = useState({
    stopPolling: false,
    showRetry: false,
    isLoaderOpen: isOpen,
  });

  const handleStopPolling = (): void => {
    setState((prev) => ({
      ...prev,
      showRetry: true,
      isLoaderOpen: false,
      stopPolling: true,
    }));
  };

  const handleFetchCustomerDetails = async (): Promise<void> => {
    const customers = await fetchCustomerDetails(
      {
        [CUSTOMER_ACCESS_TOKEN_KEY]: userAccessToken ?? '',
      },
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        with_lender: selectedTenure?.provider ?? '',
      },
    );

    // Check if we received the customer details and lender details with emandate as true
    // If emandate is now true update the user details and close the modal
    if (customers.success && customers.data) {
      const { data } = customers;
      if (data.lender_details) {
        const lenderStatus = getLenderDetailsFromTenure({
          provider: selectedTenure?.provider as string,
          lenderDetails: data.lender_details,
        });

        if (lenderStatus?.repayment && isLenderStepCompleted(lenderStatus.repayment)) {
          // stop polling
          setState({
            ...state,
            stopPolling: true,
          });

          // close the popup
          onSuccess();
          onDismiss();
        }
      }
    } else if (customers.error) {
      // stop polling and show retry modal to retry emandate payment
      handleStopPolling();
    }
  };

  const handleRetry = (): void => {
    // Call the onRetry method which will trigger the emandate flow again
    onRetry();
    // close the polling popup
    onDismiss();
  };

  // useInterval({
  //   callback: handleFetchCustomerDetails,
  //   delay: EMANDATE_POLLING_INTERVAL,
  //   stopPolling: state.stopPolling,
  //   onStop: handleStopPolling,
  // });

  const fetchEMandateStatus = async (): Promise<void> => {
    try {
      const authorizeStatus = await getEmandateStatus(repaymentId as string);
      if (authorizeStatus.success) {
        // stop polling
        setState({
          ...state,
          stopPolling: true,
        });

        // close the popup
        onSuccess();
        onDismiss();
      } else {
        handleStopPolling();
      }
    } catch {
      handleStopPolling();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // fetch emandate status
    fetchEMandateStatus();
  }, []);

  return (
    <>
      <Modal isOpen={state.isLoaderOpen} onDismiss={onDismiss} showClose={false}>
        <Modal.Body>
          <div className="flex flex-col items-center">
            <p className="text-center text-base text-on-grey-subtle">
              Emandate Details are being verified with
            </p>
            <h2 className="font-tasa font-semibold text-xl block text-on-grey-normal mt-1">
              {LENDER_MAPPING[selectedTenure?.provider as LENDER_NAMES]}
            </h2>

            <Image
              className="animate-spin mt-12 mb-12"
              src="/spinner.svg"
              alt="spinner"
              width={41}
              height={41}
            />
          </div>
          <SecuredByRazorpay />
        </Modal.Body>
      </Modal>
      {state.showRetry ? (
        <ErrorModal
          isOpen={state.showRetry}
          onDismiss={(): void => {
            setState({
              ...state,
              showRetry: false,
            });
          }}
          title={`e-Mandate verification failed with ${
            LENDER_MAPPING[selectedTenure?.provider as LENDER_NAMES]
          }`}
          description="Due to technical failure or name mismatch (account name not matching with your Aadhar or PAN)"
          ctaText="Retry"
          onCtaClick={handleRetry}
        />
      ) : null}
    </>
  );
};

export default EMandatePollingPopup;
